import { effect, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export abstract class FormEditorBase<TValue> implements ControlValueAccessor {
    // Core signals
    readonly selectedValue = signal<TValue | undefined>(undefined);
    readonly isReadOnly = signal<boolean>(false);
    readonly placeholder = signal<string>('Add Item');
    readonly items = signal<any[]>([]);
    readonly selectedItems = signal<any[]>([]);

    // Signal for parent communication
    readonly valueChange = signal<TValue | undefined>(undefined);

    // ControlValueAccessor callbacks
    protected onChange!: (value: TValue) => void;
    protected onTouched!: () => void;

    constructor() {
        this.setupEffects();
    }

    // Public methods
    onSelectedItemsChange(items?: any[]): void {
        if (!items) return;

        this.selectedItems.set(items);
        const value = this.mapSelectedItemsToValue();
        if (this.onChange) this.onChange(value as TValue);
        if (this.onTouched) this.onTouched();
        this.valueChange.set(value);
    }

    resetForm(): void {
        this.selectedItems.set([]);
        this.selectedValue.set(undefined as any);
        this.valueChange.set(undefined);
    }

    // ControlValueAccessor implementation
    writeValue(value: TValue): void {
        this.selectedValue.set(value);
        if (value == null) {
            this.selectedItems.set([]);
            return;
        }
        if (Array.isArray(value) && value.length === 0) {
            this.selectedItems.set([]);
            return;
        }
        const items = this.mapValueToSelectedItems(value);
        this.selectedItems.set(items);
    }

    registerOnChange(fn: (value: TValue) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isReadOnly.set(isDisabled);
    }

    // Abstract methods that must be implemented by child classes
    protected abstract onEntityTextChange(text: string): Promise<void>;
    protected abstract mapSelectedItemsToValue(): TValue;
    protected abstract mapValueToSelectedItems(value: TValue): any[];

    private setupEffects(): void {
        // Handle selected items changes
        effect(() => {
            const value = this.mapSelectedItemsToValue();
            if (this.onChange) this.onChange(value as TValue);
            if (this.onTouched) this.onTouched();
            this.valueChange.set(value);
        });

        // Handle read-only state changes
        effect(() => {
            this.isReadOnly();
        });
    }
}

export function createValueAccessorProvider(componentClass: any) {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => componentClass),
        multi: true
    };
}
